/**
 * Audio Stream Processor
 *
 * Processes a MediaStream's audio track through RNNoise for noise suppression.
 * Returns a new MediaStream with cleaned audio that can be used for WebRTC and STT.
 *
 * Flow:
 * Microphone → AudioContext → ScriptProcessor/AudioWorklet → RNNoise → MediaStreamDestination → Processed Stream
 */

import { getAudioProcessor } from './audioProcessor.js';

// Singleton state
let audioContext = null;
let sourceNode = null;
let processorNode = null;
let destinationNode = null;
let processedStream = null;
let originalStream = null;
let isProcessing = false;
let audioProcessorInstance = null;

// Processing stats
let processedFrames = 0;
let lastStatsTime = 0;

/**
 * Start processing an audio stream through noise suppression
 *
 * @param {MediaStream} inputStream - The original microphone stream
 * @returns {Promise<MediaStream>} - A new stream with noise-suppressed audio
 */
export async function startAudioStreamProcessing(inputStream) {
  if (isProcessing) {
    console.log('[AudioStreamProcessor] Already processing, returning existing stream');
    return processedStream;
  }

  console.log('[AudioStreamProcessor] Starting audio stream processing...');
  originalStream = inputStream;

  try {
    // Initialize the audio processor (loads RNNoise WASM)
    audioProcessorInstance = getAudioProcessor();
    if (!audioProcessorInstance.isReady()) {
      console.log('[AudioStreamProcessor] Initializing audio processor...');
      await audioProcessorInstance.initialize();
    }

    // Check if noise suppression is actually available
    if (!audioProcessorInstance.isNoiseSuppressionActive()) {
      console.log('[AudioStreamProcessor] Noise suppression not available, returning original stream');
      return inputStream;
    }

    // Get the audio track from input stream
    const audioTrack = inputStream.getAudioTracks()[0];
    if (!audioTrack) {
      console.warn('[AudioStreamProcessor] No audio track in input stream');
      return inputStream;
    }

    // Get track settings
    const settings = audioTrack.getSettings();
    const sampleRate = settings.sampleRate || 48000;
    console.log('[AudioStreamProcessor] Input audio track settings:', settings);

    // Create AudioContext with matching sample rate
    audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: sampleRate,
    });

    console.log('[AudioStreamProcessor] AudioContext created, sample rate:', audioContext.sampleRate);

    // Create source from input stream
    sourceNode = audioContext.createMediaStreamSource(inputStream);

    // Create destination node (outputs to a new MediaStream)
    destinationNode = audioContext.createMediaStreamDestination();

    // Create processor using ScriptProcessor (more compatible)
    // Buffer size 4096 = ~85ms at 48kHz, good balance of latency and efficiency
    const bufferSize = 4096;
    processorNode = audioContext.createScriptProcessor(bufferSize, 1, 1);

    processedFrames = 0;
    lastStatsTime = Date.now();

    processorNode.onaudioprocess = (event) => {
      const inputData = event.inputBuffer.getChannelData(0);
      const outputData = event.outputBuffer.getChannelData(0);

      // Process through RNNoise
      if (audioProcessorInstance?.isReady()) {
        const processed = audioProcessorInstance.process(inputData, audioContext.sampleRate);

        // Copy processed data to output
        // Handle case where processed length might differ due to resampling
        const copyLength = Math.min(processed.length, outputData.length);
        for (let i = 0; i < copyLength; i++) {
          outputData[i] = processed[i];
        }
        // Fill remaining with zeros if needed
        for (let i = copyLength; i < outputData.length; i++) {
          outputData[i] = 0;
        }
      } else {
        // Passthrough if processor not ready
        outputData.set(inputData);
      }

      processedFrames++;

      // Log stats every 5 seconds
      const now = Date.now();
      if (now - lastStatsTime > 5000) {
        const fps = processedFrames / ((now - lastStatsTime) / 1000);
        console.log(`[AudioStreamProcessor] Processing ${fps.toFixed(1)} frames/sec`);
        processedFrames = 0;
        lastStatsTime = now;
      }
    };

    // Connect the audio graph
    sourceNode.connect(processorNode);
    processorNode.connect(destinationNode);

    // Also connect to destination for monitoring (optional, can be removed)
    // processorNode.connect(audioContext.destination);

    // Get the processed stream
    processedStream = destinationNode.stream;

    // Copy video tracks from original stream if any
    const videoTracks = inputStream.getVideoTracks();
    if (videoTracks.length > 0) {
      // Create a new stream with both processed audio and original video
      const combinedStream = new MediaStream();
      processedStream.getAudioTracks().forEach(track => combinedStream.addTrack(track));
      videoTracks.forEach(track => combinedStream.addTrack(track));
      processedStream = combinedStream;
    }

    isProcessing = true;
    console.log('[AudioStreamProcessor] Audio stream processing started');
    console.log('[AudioStreamProcessor] Processed stream tracks:', processedStream.getTracks().map(t => t.kind));

    return processedStream;

  } catch (error) {
    console.error('[AudioStreamProcessor] Failed to start processing:', error);
    // Return original stream on error
    return inputStream;
  }
}

/**
 * Stop processing and clean up resources
 */
export function stopAudioStreamProcessing() {
  console.log('[AudioStreamProcessor] Stopping audio stream processing...');

  isProcessing = false;

  if (processorNode) {
    processorNode.disconnect();
    processorNode.onaudioprocess = null;
    processorNode = null;
  }

  if (sourceNode) {
    sourceNode.disconnect();
    sourceNode = null;
  }

  if (destinationNode) {
    destinationNode.disconnect();
    destinationNode = null;
  }

  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
    audioContext = null;
  }

  processedStream = null;
  originalStream = null;

  console.log('[AudioStreamProcessor] Stopped');
}

/**
 * Get the currently processed stream
 */
export function getProcessedStream() {
  return processedStream;
}

/**
 * Get the original unprocessed stream
 */
export function getOriginalStream() {
  return originalStream;
}

/**
 * Check if processing is active
 */
export function isAudioProcessingActive() {
  return isProcessing && audioProcessorInstance?.isNoiseSuppressionActive();
}

/**
 * Get processing status
 */
export function getProcessingStatus() {
  return {
    isProcessing,
    noiseSuppressionActive: audioProcessorInstance?.isNoiseSuppressionActive() || false,
    sampleRate: audioContext?.sampleRate || null,
    provider: audioProcessorInstance?.getState()?.noiseSuppressionProvider || null,
  };
}

export default {
  startAudioStreamProcessing,
  stopAudioStreamProcessing,
  getProcessedStream,
  getOriginalStream,
  isAudioProcessingActive,
  getProcessingStatus,
};
